import {
    decimalsToBps,
    TransactionSettingsHeader,
    VersionedTransactionResponse,
    AnalyticsInfo
} from "@bridgesplit/utils";
import { ParsedTransactionInstruction } from "@loopscale/pandora-types";

import { StrategyDuration } from "./time";
import { ExternalYieldSource } from "./external-yield";
import {
    BorrowPrincipalRequested,
    DepositCollateralInput,
    IncreaseLeverageTransactionInput,
    LoanCreationParams
} from "./loop";
import { SdkApiLoanInfo } from "./api";

export interface AbfTransactionHeaders {
    groupIdentifier: string | undefined;
    bearerToken: string | undefined;
    userWallet: string | undefined;
    txnSettings: TransactionSettingsHeader | undefined;
}

export interface BorrowPrincipalParams {
    amount: number;
    expectedLoanValues: ExpectedLoanValues;
    duration: number;
}

export interface BorrowPrincipalTxnParams {
    loan: string;
    borrowParams: BorrowPrincipalParams;
    refinanceParams?: BorrowMoreRefinanceParams;
    strategy: string;
}

export interface BorrowMoreRefinanceParams {
    newStrategy: string;
    ledgerIndex: number;
    durationIndex: number;
}

export interface RepayLoanParams {
    amount: number;
    ledgerIndex: number;
    repayAll: boolean;
}

export interface WithdrawCollateralParams {
    collateralMint: string;
    amount: number;
    closeIfEligible?: boolean;
}

export interface RepayLoanTxnParams {
    loan: string;
    repayParams: RepayLoanParams[];
    collateralWithdrawalParams: WithdrawCollateralParams[] | undefined;
    cpiIxs: ParsedTransactionInstruction[];
    cpiLuts: string[];
    cpiSigners: string[];
    mintToReceive: string;
    userPublicKey: string;
    loopName?: string;
    simulate?: boolean;
    analyticsInfo?: AnalyticsInfo;
    setupIxs?: ParsedTransactionInstruction[];
    setupLuts?: string[];
    setupSigners?: string[];
    unifySetup?: boolean;
    closeIfPossible?: boolean;
    expectedLoanInfo?: SdkApiLoanInfo;
    /// Optional vault LP stake leg to compose with this txn as a suffix.
    stakeInstructions?: StakeLpQueryParams;
}

export type DepositCollateralParams = {
    loan: string;
    depositMint: string;
    decimals: number;
    amount: number;
    assetType: number;
    assetIdentifier: string;
    expectedLoanValues: ExpectedLoanValues;
    loanCreationParams?: LoanCreationParams;
    /// Optional vault LP unstake leg to compose with this txn. If provided, it's treated as a prefix.
    unstakeInstructions?: UnstakeLpQueryParams;
};

export interface WithdrawCollateralTxnParams {
    loan: string;
    collateralMint: string;
    amount: number;
    collateralIndex: number;
    assetIndexGuidance?: string; //Do not need to add derived in BE
    expectedLoanValues: ExpectedLoanValues;
    /// Optional vault LP stake leg to compose with this txn as a suffix.
    stakeInstructions?: StakeLpQueryParams;
}

export interface RefinanceLoanTxnParams {
    loan: string;
    oldStrategy: string;
    newStrategy: string;
    principalMint: string;
    refinanceParams: {
        ledgerIndex: number;
        durationIndex: number;
    };
}

export interface MergeLoanTxnParams {
    baseLoan: string;
    branchLoan: string;
}

export enum ManageSide {
    Deposit,
    Withdraw
}

export type MultiCollateralTermsUpdateParams = Array<
    [
        number, // u64 APY
        Array<[number, number]> //[Index of collateral in market information, duration index]
    ]
>;

export interface CreateCustomStrategyTxnParams {
    principalMint: string;
    lender: string;
    amount: number;
    externalYieldSourceArgs?: ExternalYieldSourceArgs;
    liquidityBuffer?: number;
    interestFee?: number;
    originationFee?: number;
    originationCap?: number;
    collateralTerms?: MultiCollateralTermsUpdateParams;
    marketInformation?: string;
}

export interface ExternalYieldSourceArgs {
    newExternalYieldSource: ExternalYieldSource;
    createExternalYieldAccount: boolean;
}

export interface WithdrawStrategyTxnParams {
    strategy: string;
    amount: number;
    withdrawAll: boolean;
}

export interface DepositStrategyTxnParams {
    strategy: string;
    amount: number;
}

export interface CloseStrategyTxnParams {
    strategyAddress: string;
}

export type StakeLpQueryParams = {
    vault: string;
    amount: number;
    stakeAccount?: string;
};

export type UnstakeLpQueryParams = {
    vault: string;
    amount: number;
    stakeAccount?: string;
};

export interface CreateLoanParams {
    borrower: string;
    depositCollateral: DepositCollateralInput[];
    principalRequested: BorrowPrincipalRequested[];
    assetIndexGuidance: number[];
    loanNonce?: number;
    isLoop?: boolean;
    /// Optional vault LP unstake leg to compose with this txn as a prefix.
    unstakeInstructions?: UnstakeLpQueryParams;
}

export type LoanValueTuple = [number, number, number, number, number]; // TS cannot handle const for array length

// By the time a loanValueTuple needs to be converted to bps the following key should be used:
// NUMBER.MAX_SAFE_INTEGER indicates that a value is DISABLED_APY
// Any other number indicates that the value is enabled and should be converted to bps, including 0.
export function convertLoanValueTupleToBps(loanValueTuple: LoanValueTuple): LoanValueTuple {
    const newTuple: LoanValueTuple = [0, 0, 0, 0, 0]; // TS cannot handle const for array length so need to use this fn
    for (let i = 0; i < loanValueTuple.length; i++) {
        newTuple[i] =
            loanValueTuple[i] === Number.MAX_SAFE_INTEGER ? Number.MAX_SAFE_INTEGER : decimalsToBps(loanValueTuple[i]);
    }
    return newTuple;
}

export interface ExpectedLoanValues {
    expectedApy: number;
    expectedLqt: LoanValueTuple;
}

export interface SellLoanParams {
    loan: string;
    oldStrategy: string;
    newStrategy: string;
    sellParams: {
        ledgerIndex: number;
        expectedSalePrice: number;
        buyerDurationIndex: number;
    };
}

export interface BulkStrategyDepositParams {
    strategy: string;
    amount: number;
}

export interface BulkStrategyWithdrawParams {
    strategy: string;
    amount: number;
    withdrawAll: boolean;
}

export type LenderBuildStep =
    | { type: "sell"; params: SellLoanParams }
    | { type: "strategyDeposit"; params: BulkStrategyDepositParams }
    | { type: "strategyWithdraw"; params: BulkStrategyWithdrawParams };

export interface LenderBuildRequest {
    loan?: string;
    steps: LenderBuildStep[][];
}

export interface LenderBuildResponse {
    transactions: VersionedTransactionResponse[];
}

export interface VaultDepositParams {
    principalAmount: number;
    minLpAmount: number;
    vault: string;
}

export interface VaultWithdrawParams {
    maxAmountLp: number;
    vault: string;
    amountPrincipal: number;
    withdrawAll: boolean;
}

export interface EditCustomStrategyTxnParams {
    strategy: string;
    collateralTerms?: StrategyCollateralUpdates;
    updateParams?: EditStrategySettingsArgs;
}

export interface EditStrategySettingsArgs {
    originationsEnabled?: boolean;
    liquidityBuffer?: number;
    interestFee?: number;
    originationFee?: number;
    principalFee?: number;
    originationCap?: number;
    externalYieldSource?: ExternalYieldSourceParams;
}

export interface ExternalYieldSourceParams {
    newExternalYieldSource: number;
}

export interface InitVaultTimelockTxnParams {
    vaultAddress: string;
    collateralUpdates?: StrategyCollateralUpdates;
    editStrategyArgs?: EditStrategyParams;
    editVaultArgs?: EditVaultParams;
    updatePrincipalCaps?: UpdatePrincipalCapsArgs;
}

export interface EditStrategyParams {
    originationsEnabled: boolean;
    nonce?: string;
    externalYieldSource: ExternalYieldSourceParams;
    principalMint?: string;
    originationFee?: number;
    interestFee?: number;
    originationCap?: number;
    liquidityBuffer?: number;
}

export interface EditVaultParams {
    maxEarlyUnstakeFeeCbps: number;
}

export interface StrategyCollateralUpdates {
    addCollateral?: Record<string, AddCollateralArgs>;
    removeCollateral?: Record<string, RemoveCollateralArgs>;
    updateCollateral?: Record<string, CollateralParamsUpdateArgs>;
    updateAssetAllocation?: UpdateAssetAllocationArgs[];
}

export interface AddCollateralArgs {
    durationsAndApys: Record<string, string>;
    externalMarketInformationAddress?: string;
    marketInformation?: UpdateMarketInformationArgs;
}

export interface RemoveCollateralArgs {
    durations: StrategyDuration[];
    removeFromMarketInformation: boolean;
}

export interface CollateralParamsUpdateArgs {
    ltvUpdate?: UpdateMarketInformationArgs;
    apyUpdate?: Record<string, string>;
}

export interface UpdateMarketInformationArgs {
    assetIdentifier: string;
    maxCollateralAllocationPct?: string;
    quoteMint?: string;
    oracleAccount?: string;
    oracleType?: number;
    maxUncertainty?: number;
    maxAge?: number;
    decimals?: number;
    ltv?: number;
    liquidationThreshold?: number;
    removeCollateral?: boolean;
}

export interface CancelVaultTimeLockTxnParams {
    timelock: string;
    manager: string;
    vault: string;
}

export interface ClaimVaultFeesTxnParams {
    lendVaultAddress: string;
}

export interface UpdateLendingVaultDepositsTxnParams {
    lendVaultAddress: string;
    depositsEnabled: boolean;
}

export interface CreateRewardsTxnParams {
    vaultAddress: string;
    amountToDeposit: number;
    rewardMint: string;
    rewardStartTime: number;
    rewardEndTime: number;
    durationStakeWeights: number[];
}

export interface UpdateRewardsScheduleTxnParams {
    vaultAddress: string;
    scheduleIndex: number;
    amountToDeposit: number;
    newScheduleEndTime?: number | null; //Pass in null to keep the same end time
    rewardMint: string;
}

export interface ClaimLendVaultRewardsParams {
    vaultAddress: string;
    unclaimedStakeAccounts?: string[];
    rewardMints?: string[];
}

export interface SwapCollateralTxnParams {
    loan: string;
    depositCollateral: DepositCollateralInput[];
    withdrawCollateral: WithdrawCollateralParams[];
    cpiIxs?: ParsedTransactionInstruction[];
    cpiLuts?: string[];
    cpiSigners?: string[];
    // Setup slot mirrors flash_borrow: when unifySetup is false and setupIxs is non-empty, BE
    // emits a separate setup tx ahead of the main. Populate from pandora's hoisted swap-step
    // setupIxs (see splitAtaCreates on the pandora request).
    setupIxs?: ParsedTransactionInstruction[];
    setupLuts?: string[];
    setupSigners?: string[];
    unifySetup?: boolean;
}

export interface UpdatePrincipalCapsArgs {
    max1HrBorrowCap: string;
    max24HrBorrowCap: string;
    maxOutstandingBorrowCap: string;
    max1HrSupplyCap: string;
    max24HrSupplyCap: string;
    maxOutstandingSupplyCap: string;
    max1HrWithdrawCap: string;
    max24HrWithdrawCap: string;
    //No global supply cap
}

export interface UpdateAssetAllocationArgs {
    assetIdentifier: string;
    maxAllocationPct: string; //CBPS
}

export interface LoanLutQuery {
    loan: string;
    actionType: number;
}

export type LoanPnlRequest = {
    loans: string[];
    includePnl?: boolean;
    // Graph data parameters
    startTimestamp?: number;
    endTimestamp?: number;
    intervalSeconds?: number;
    maxPoints?: number;
    projectionSeconds?: number;
    includeGraphPnl?: boolean;
    includeGraphPositionValue?: boolean;
    includeGraphRateHistory?: boolean;
};

/** Marker type for charts. Matches Rust `ChartMarkerType` (camelCase). */
export enum ChartMarkerType {
    Rollover = "rollover"
}

/** Rollover payload for `ChartMarkerMetadata` (payload-only; marker kind is top-level `type`). */
export type ChartMarkerMetadataRollover = {
    previousCollateralMint: string;
    newCollateralMint: string;
};

/** Discriminated union of chart marker payloads — extend when adding `ChartMarkerType` variants. */
export type ChartMarkerMetadata = ChartMarkerMetadataRollover;

/**
 * Annotation for a graph: `date` + discriminated payload (matches Rust `ChartDataMarker`).
 * Top-level on `LoanPnlStats` (same list for every chart).
 */
export type ChartDataMarker = {
    date: number;
    type: ChartMarkerType;
    metadata?: ChartMarkerMetadata;
};

export type PnLDataPoint = {
    date: number;
    netPositionValueUsd?: number;
    netPositionValueTokenAmount?: number;
    netCollateralTransferUsdAmount?: number;
    netCollateralTransferTokenAmount?: number;
    usdPnl?: number;
    tokenPnl?: number;
    collateralPrice?: number;
    collateralValueUsd?: number;
    principalValueUsd?: number;
    isProjection: boolean;
};

export type PositionValueDataPoint = {
    date: number;
    netPositionValueUsd?: number;
    netPositionValueTokenAmount?: number;
    collateralValueUsd?: number;
    principalValueUsd?: number;
    collateralTokenAmount?: number;
    collateralTokenAmountOfDebt?: number;
    isProjection: boolean;
    collateralMint?: string;
};

export type RateHistoryDataPoint = {
    date: number;
    netApy?: number;
    collateralApy?: number;
    borrowApy?: number;
    isProjection: boolean;
    collateralMint?: string;
};

export type LoanPnlStats = {
    collateralPnl?: number;
    usdPnl?: number;
    /** In-kind PnL re-priced at the current collateral spot price (`collateralPnl_ui ×
     *  currentCollateralPrice`) — the value the loop card shows for non-leverage loops.
     *  `undefined` for non-loop loans, CLMM/LP collateral, or missing price (fall back to `usdPnl`). */
    tokenUsdPnl?: number;
    usdPnlPctChange?: number;
    collateralPnlPctChange?: number;
    netCollateralUsdInflow?: number;
    netCollateralInflow?: number;
    netPrincipalUsdInflow?: number;
    netPrincipalInflow?: number;
    initialUserCollateralContribution?: number;
    initialUserCollateralContributionUsd?: number;
    collateralPriceFromDatapoint?: number;
    /** Rollover and other chart annotations (derived from PnL series; shared across graphs). */
    markers?: ChartDataMarker[];
    // Graph data
    pnlData?: {
        dataPoints: PnLDataPoint[];
    };
    positionValueData?: {
        dataPoints: PositionValueDataPoint[];
    };
    rateHistoryData?: {
        dataPoints: RateHistoryDataPoint[];
    };
};

export interface PrincipalCollateralPair {
    principalAssetIdentifier: string;
    collateralAssetIdentifier: string;
}

export type LoanBuildStep =
    | { type: "flashBorrow"; params: IncreaseLeverageTransactionInput }
    | { type: "swapCollateral"; params: SwapCollateralTxnParams }
    | { type: "repay"; params: RepayLoanTxnParams };

export interface BuildLoanTransactionsRequest {
    loanAddress?: string;
    steps: LoanBuildStep[];
}

export interface CreateLoanReturn {
    transaction: VersionedTransactionResponse;
    transactions?: VersionedTransactionResponse[];
    loanAddress: string;
}

export interface BuildLoanTransactionsResponse {
    loanAddress: string;
    transactions: VersionedTransactionResponse[];
}
